import React, { useState, useEffect } from 'react';
import { emailTemplatesAPI, emailHistoryAPI, EmailTemplate, Client } from '../../services/api';
import { XMarkIcon, PaperAirplaneIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { Modal, Button, Badge } from '../ui';
import ClientRecipientSelector, { getDefaultClientRecipients } from './ClientRecipientSelector';

interface EmailComposerProps {
  client?: Client;
  onClose: () => void;
  onSent?: () => void;
}

const EmailComposer: React.FC<EmailComposerProps> = ({ client, onClose, onSent }) => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [formData, setFormData] = useState({
    to: getDefaultClientRecipients(client),
    cc: [] as string[],
    bcc: [] as string[],
    subject: '',
    body: '',
    scheduledFor: '',
  });
  const [loading, setLoading] = useState(false);
  const [showCcBcc, setShowCcBcc] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await emailTemplatesAPI.getAll({ isActive: true, limit: 100 });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setFormData(prev => ({
      ...prev,
      subject: template.subject,
      body: template.body,
    }));
  };

  const handleSend = async () => {
    if (formData.to.length === 0) {
      alert('Please enter at least one recipient');
      return;
    }

    if (!formData.subject || !formData.body) {
      alert('Please enter subject and body');
      return;
    }

    setLoading(true);
    try {
      await emailHistoryAPI.send({
        to: formData.to,
        cc: formData.cc,
        bcc: formData.bcc,
        subject: formData.subject,
        body: formData.body,
        templateId: selectedTemplate?.id,
        clientId: client?.id,
        scheduledFor: formData.scheduledFor || undefined,
      });

      alert(formData.scheduledFor ? 'Email scheduled successfully!' : 'Email sent successfully!');
      onSent?.();
      onClose();
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email');
    } finally {
      setLoading(false);
    }
  };

  const addRecipient = (field: 'to' | 'cc' | 'bcc', email: string) => {
    if (email && !formData[field].includes(email)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], email],
      }));
    }
  };

  const removeRecipient = (field: 'to' | 'cc' | 'bcc', email: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(e => e !== email),
    }));
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Compose Email" size="xl">
      <div className="space-y-6">
        {/* Template Selector */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Use Template (Optional)
          </label>
          <div className="flex items-center gap-2">
            <select
              value={selectedTemplate?.id || ''}
              onChange={(e) => {
                const template = templates.find(t => t.id === parseInt(e.target.value));
                if (template) handleTemplateSelect(template);
              }}
              className="flex-1 px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            >
              <option value="">Select a template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name} ({template.category})
                </option>
              ))}
            </select>
            {selectedTemplate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedTemplate(null);
                  setFormData(prev => ({ ...prev, subject: '', body: '' }));
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {client && (
          <ClientRecipientSelector
            client={client}
            value={formData.to}
            onChange={(to) => setFormData(prev => ({ ...prev, to }))}
          />
        )}

        {/* To Field */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            To <span className="text-error-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.to.map((email) => (
              <Badge key={email} variant="primary" size="md" className="flex items-center gap-1">
                {email}
                <button
                  onClick={() => removeRecipient('to', email)}
                  className="ml-1 hover:text-primary-900"
                >
                  <XMarkIcon className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <input
            type="email"
            placeholder="Add recipient email..."
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addRecipient('to', e.currentTarget.value);
                e.currentTarget.value = '';
              }
            }}
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          />
        </div>

        {/* CC/BCC Toggle */}
        {!showCcBcc && (
          <button
            onClick={() => setShowCcBcc(true)}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            + Add CC/BCC
          </button>
        )}

        {/* CC Field */}
        {showCcBcc && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">CC</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.cc.map((email) => (
                <Badge key={email} variant="neutral" size="md" className="flex items-center gap-1">
                  {email}
                  <button
                    onClick={() => removeRecipient('cc', email)}
                    className="ml-1 hover:text-neutral-900"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <input
              type="email"
              placeholder="Add CC email..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRecipient('cc', e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>
        )}

        {/* BCC Field */}
        {showCcBcc && (
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">BCC</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.bcc.map((email) => (
                <Badge key={email} variant="neutral" size="md" className="flex items-center gap-1">
                  {email}
                  <button
                    onClick={() => removeRecipient('bcc', email)}
                    className="ml-1 hover:text-neutral-900"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
            <input
              type="email"
              placeholder="Add BCC email..."
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addRecipient('bcc', e.currentTarget.value);
                  e.currentTarget.value = '';
                }
              }}
              className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            />
          </div>
        )}

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Subject <span className="text-error-500">*</span>
          </label>
          <input
            type="text"
            required
            value={formData.subject}
            onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            placeholder="Email subject"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Message <span className="text-error-500">*</span>
          </label>
          <textarea
            required
            rows={10}
            value={formData.body}
            onChange={(e) => setFormData(prev => ({ ...prev, body: e.target.value }))}
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
            placeholder="Email body"
          />
        </div>

        {/* Schedule */}
        <div>
          <label className="block text-sm font-medium text-neutral-700 mb-2">
            Schedule Send (Optional)
          </label>
          <input
            type="datetime-local"
            value={formData.scheduledFor}
            onChange={(e) => setFormData(prev => ({ ...prev, scheduledFor: e.target.value }))}
            className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-neutral-200">
          <div className="text-sm text-neutral-600">
            {client && (
              <span className="flex items-center">
                <DocumentTextIcon className="h-4 w-4 mr-1" />
                Sending to: {client.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSend}
              isLoading={loading}
              leftIcon={<PaperAirplaneIcon className="h-5 w-5" />}
            >
              {formData.scheduledFor ? 'Schedule' : 'Send'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EmailComposer;
